/** Index of every report, so nothing is only reachable through the sidebar. */
import { Link } from 'react-router-dom'
import { PageHeader } from '../App'
import { Card } from '../components/ui'
import { Icon } from '../components/Icon'
import { useRelabel } from '../lib/terms'

const REPORTS = [
  {
    to: '/reports/newsletters-by-partner',
    icon: 'list',
    title: 'Updates by Partner Unit',
    body: 'Pick a partner unit and read everything they have sent. Collapsed one line per update, expandable, with their photo and country alongside. Read only.',
  },
  {
    to: '/reports/donors-by-partner',
    icon: 'heart',
    cap: 'manageSettings',
    title: 'Donors by Partner Unit',
    body: 'Which partner units are supported and by whom, from the giving register — including the ones with no donor recorded. Exports one line per donor and unit.',
  },
  {
    to: '/reports/coverage',
    icon: 'donut',
    title: 'Coverage & Gaps',
    body: 'Who has gone quiet. Partner units bucketed by how long since their last update, and the registry rows that have never sent one.',
  },
  {
    to: '/reports/activity',
    icon: 'trend',
    title: 'Activity over Time',
    body: 'Updates per year and month, distinct partner units writing, average length, and the day of the week they arrive on.',
  },
  {
    to: '/reports/timeline',
    icon: 'clock',
    title: 'Timeline',
    body: 'A month-by-month heatmap of the whole archive, plus the milestones at either end of each year.',
  },
  {
    to: '/reports/countries',
    icon: 'compare',
    title: 'Country Comparison',
    body: 'Countries side by side, including the cases where an update records a different country from the partner unit registry.',
  },
  {
    to: '/reports/text',
    icon: 'text',
    title: 'Text Analytics',
    body: 'Length distribution, how updates have changed size over the years, and prayer request coverage.',
  },
  {
    to: '/reports/ai-usage',
    icon: 'ai',
    title: 'AI Usage',
    body: 'Every prompt run: which model, how long it took, what failed.',
  },
  {
    to: '/data-quality',
    icon: 'warning',
    title: 'Data Quality',
    body: 'Everything the import flagged — codes missing from the registry, country disagreements, duplicate dates, incomplete partner unit records.',
  },
  {
    to: '/prayer-requests',
    icon: 'prayer',
    title: 'Prayer Requests',
    body: 'Just the prayer requests, filterable by partner unit, country and date.',
  },
  {
    to: '/highlighted-needs',
    icon: 'star',
    title: 'Highlighted Updates',
    body: 'Everything carrying the highlighted flag from the source data.',
  },
  {
    to: '/',
    icon: 'search',
    title: 'Search Everything',
    body: 'Full-text search across every update body and prayer request, from the box at the top of the dashboard. Press / from any page to jump to it.',
  },
]

export default function ReportsIndex({ user }) {
  const t = useRelabel()
  /* A card for a page the role cannot open is a dead end, so the one report with a
     capability is filtered here as well as in the sidebar. */
  const shown = REPORTS.filter((r) => !r.cap || user?.caps?.[r.cap])
  return (
    <PageHeader title="Reports" sub="Every view of the archive in one place">
      <div className="grid grid-3">
        {shown.map((r) => (
          <Link key={r.to} to={r.to} style={{ textDecoration: 'none', color: 'inherit' }}>
            <Card className="report-tile">
              <div className="row row-tight" style={{ marginBottom: 6 }}>
                <Icon name={r.icon} size={20} className="icon report-tile-icon" />
                <h3 style={{ color: 'var(--accent)' }}>{t(r.title)}</h3>
              </div>
              <p className="small muted">{t(r.body)}</p>
            </Card>
          </Link>
        ))}
      </div>
    </PageHeader>
  )
}
